import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

// Custom piece configurations - basic shapes with customizable fill/stroke
export const CUSTOM_PIECE_CONFIGS = {
  piece_rectangle: {
    label: 'Rectangle',
    width: 100,
    height: 50,
    minWidth: 40,
    minHeight: 30,
    defaultSeats: 0,
    isCircle: false,
  },
  piece_square: {
    label: 'Square',
    width: 60,
    height: 60,
    minWidth: 40,
    minHeight: 40,
    defaultSeats: 0,
    isCircle: false,
  },
  piece_circle: {
    label: 'Circle',
    width: 60,
    height: 60,
    minWidth: 40,
    minHeight: 40,
    isCircle: true,
    defaultSeats: 0,
  },
  piece_seat: {
    label: 'Seat',
    width: 30,
    height: 30,
    minWidth: 20,
    minHeight: 20,
    isCircle: true,
    defaultSeats: 1,
    isSeat: true,
  },
  piece_wave: {
    label: 'Arc',
    width: 120,
    height: 60,
    minWidth: 60,
    minHeight: 30,
    defaultSeats: 0,
    isCircle: false,
    isArc: true,
    svgPath: 'M422.7,276.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6Z',
    viewBox: '270 260 320 160',
  },
};

// Resize handle component
function ResizeHandle({ position, onMouseDown }) {
  const cursorMap = {
    'nw': 'nw-resize', 'n': 'n-resize', 'ne': 'ne-resize',
    'w': 'w-resize', 'e': 'e-resize',
    'sw': 'sw-resize', 's': 's-resize', 'se': 'se-resize',
  };

  const positionStyles = {
    'nw': { top: -4, left: -4 },
    'n': { top: -4, left: '50%', transform: 'translateX(-50%)' },
    'ne': { top: -4, right: -4 },
    'w': { top: '50%', left: -4, transform: 'translateY(-50%)' },
    'e': { top: '50%', right: -4, transform: 'translateY(-50%)' },
    'sw': { bottom: -4, left: -4 },
    's': { bottom: -4, left: '50%', transform: 'translateX(-50%)' },
    'se': { bottom: -4, right: -4 },
  };

  return (
    <div
      className="absolute w-2 h-2 bg-white border border-blue-500 z-20"
      style={{
        ...positionStyles[position],
        cursor: cursorMap[position],
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        onMouseDown(e, position);
      }}
    />
  );
}

export default function CustomPieceShape({
  piece,
  zoom = 1,
  isSelected = false,
  onClick,
  onPositionChange,
  onSizeChange,
  onDuplicate,
  onDelete,
  onSeatsChange, // callback to change number of seats: (pieceId, newSeats) => void
  onSeatOffsetsChange, // callback to save seat offsets: (pieceId, seatOffsets) => void
  gridSize = 20,
  showSeats = true,
  parentTable = null, // If set, piece is part of a grouped table
  guests = [], // guests for showing on seats
  onSeatClick, // callback for Quick Assign: (seatInfo) => void
}) {
  const pieceRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [rotateStart, setRotateStart] = useState(null);
  const [hoveredSeatIndex, setHoveredSeatIndex] = useState(null);
  const [isDraggingSeat, setIsDraggingSeat] = useState(false);
  const [draggedSeatIndex, setDraggedSeatIndex] = useState(null);
  const [seatDragStart, setSeatDragStart] = useState(null);
  const [localSeatOffsets, setLocalSeatOffsets] = useState(piece.seat_offsets || {});
  
  // Manual drag state for rotated pieces (bypasses react-dnd issues)
  const [isManualDragging, setIsManualDragging] = useState(false);
  const [manualDragStart, setManualDragStart] = useState(null);
  const [manualDragOffset, setManualDragOffset] = useState({ x: 0, y: 0 });

  // When piece is part of a group, it should still be interactive for seats but not movable
  const isGrouped = !!parentTable;
  
  // Get tableId for Quick Assign - use parentTable if grouped, otherwise null
  const tableIdForSeats = isGrouped ? parentTable.id : null;
  const tableNameForSeats = isGrouped ? (parentTable.table_name || 'Grouped Table') : (piece.label || 'Piece');
  
  // Get guests assigned to this table (if grouped)
  const tableGuests = isGrouped ? guests.filter(g => g.table_id === parentTable.id) : [];
  
  // Sync local offsets with piece data
  useEffect(() => {
    setLocalSeatOffsets(piece.seat_offsets || {});
  }, [piece.seat_offsets]);

  const config = CUSTOM_PIECE_CONFIGS[piece.piece_type] || CUSTOM_PIECE_CONFIGS.piece_rectangle;
  const width = piece.width || config.width;
  const height = piece.height || config.height;
  const isCircle = config.isCircle;
  const seats = piece.seats || config.defaultSeats;
  const rotation = piece.rotation || 0;
  const isRotated = rotation !== 0;

  // Drag functionality - CustomDragLayer handles the preview
  // Disable drag when piece is part of a group OR when rotated (use manual drag instead)
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'CUSTOM_PIECE',
    item: () => ({
      id: piece.id,
      type: 'CUSTOM_PIECE',
      piece_type: piece.piece_type,
      width,
      height,
      seats,
      rotation: rotation,
    }),
    canDrag: !isResizing && !isRotating && !isGrouped && !isRotated && !isManualDragging,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Hide default browser drag preview - CustomDragLayer handles it
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Snap to grid helper
  const snapToGrid = useCallback((value) => {
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);
  
  // ============ MANUAL DRAG HANDLING FOR ROTATED PIECES ============
  const handleManualDragStart = useCallback((e) => {
    if (!isRotated || isResizing || isRotating || isGrouped) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsManualDragging(true);
    setManualDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      pieceX: piece.position_x || 0,
      pieceY: piece.position_y || 0,
    });
    setManualDragOffset({ x: 0, y: 0 });
  }, [isRotated, isResizing, isRotating, isGrouped, piece.position_x, piece.position_y]);

  const handleManualDragMove = useCallback((e) => {
    if (!isManualDragging || !manualDragStart) return;
    
    const deltaX = (e.clientX - manualDragStart.mouseX) / zoom;
    const deltaY = (e.clientY - manualDragStart.mouseY) / zoom;
    
    setManualDragOffset({ x: deltaX, y: deltaY });
  }, [isManualDragging, manualDragStart, zoom]);

  const handleManualDragEnd = useCallback((e) => {
    if (!isManualDragging || !manualDragStart) return;
    
    const deltaX = (e.clientX - manualDragStart.mouseX) / zoom;
    const deltaY = (e.clientY - manualDragStart.mouseY) / zoom;
    
    const newX = snapToGrid(manualDragStart.pieceX + deltaX);
    const newY = snapToGrid(manualDragStart.pieceY + deltaY);
    
    // Update position through the callback
    if (onPositionChange) {
      onPositionChange(piece.id, newX, newY);
    }
    
    setIsManualDragging(false);
    setManualDragStart(null);
    setManualDragOffset({ x: 0, y: 0 });
  }, [isManualDragging, manualDragStart, zoom, snapToGrid, onPositionChange, piece.id]);

  // Attach global mouse event listeners for manual drag
  useEffect(() => {
    if (isManualDragging) {
      const handleMouseMove = (e) => handleManualDragMove(e);
      const handleMouseUp = (e) => handleManualDragEnd(e);
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isManualDragging, handleManualDragMove, handleManualDragEnd]);
  // ============ END MANUAL DRAG HANDLING ============

  // Handle rotation start
  const handleRotateStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRotating(true);
    
    const rect = pieceRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setRotateStart({
        centerX,
        centerY,
        startAngle: Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI),
        initialRotation: rotation,
      });
    }
  }, [rotation]);

  // Handle rotation move
  const handleRotateMove = useCallback((e) => {
    if (!isRotating || !rotateStart) return;
    
    const { centerX, centerY, startAngle, initialRotation } = rotateStart;
    const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    let newRotation = initialRotation + (currentAngle - startAngle);
    
    // Normalize to 0-360
    newRotation = ((newRotation % 360) + 360) % 360;
    
    // Snap to 15 degree increments if shift is held
    if (e.shiftKey) {
      newRotation = Math.round(newRotation / 15) * 15;
    }
    
    if (onSizeChange) {
      onSizeChange(piece.id, { rotation: Math.round(newRotation) });
    }
  }, [isRotating, rotateStart, piece.id, onSizeChange]);

  // Handle rotation end
  const handleRotateEnd = useCallback(() => {
    setIsRotating(false);
    setRotateStart(null);
  }, []);

  // Global mouse event listeners for rotation
  useEffect(() => {
    if (isRotating) {
      window.addEventListener('mousemove', handleRotateMove);
      window.addEventListener('mouseup', handleRotateEnd);
      return () => {
        window.removeEventListener('mousemove', handleRotateMove);
        window.removeEventListener('mouseup', handleRotateEnd);
      };
    }
  }, [isRotating, handleRotateMove, handleRotateEnd]);

  // Handle resize
  const handleResizeStart = useCallback((e, position) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      position,
      mouseX: e.clientX,
      mouseY: e.clientY,
      width,
      height,
      x: piece.position_x,
      y: piece.position_y,
    });
  }, [width, height, piece.position_x, piece.position_y]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeStart) return;

    const deltaX = (e.clientX - resizeStart.mouseX) / zoom;
    const deltaY = (e.clientY - resizeStart.mouseY) / zoom;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = resizeStart.x;
    let newY = resizeStart.y;

    const { position } = resizeStart;

    if (position.includes('e')) {
      newWidth = Math.max(config.minWidth, resizeStart.width + deltaX);
    }
    if (position.includes('w')) {
      const widthDelta = Math.min(deltaX, resizeStart.width - config.minWidth);
      newWidth = resizeStart.width - widthDelta;
      newX = resizeStart.x + widthDelta;
    }
    if (position.includes('s')) {
      newHeight = Math.max(config.minHeight, resizeStart.height + deltaY);
    }
    if (position.includes('n')) {
      const heightDelta = Math.min(deltaY, resizeStart.height - config.minHeight);
      newHeight = resizeStart.height - heightDelta;
      newY = resizeStart.y + heightDelta;
    }

    // Keep aspect ratio for circles
    if (isCircle) {
      const maxDim = Math.max(newWidth, newHeight);
      newWidth = maxDim;
      newHeight = maxDim;
    }

    // Snap to grid
    newWidth = snapToGrid(newWidth);
    newHeight = snapToGrid(newHeight);
    newX = snapToGrid(newX);
    newY = snapToGrid(newY);

    if (onSizeChange) {
      onSizeChange(piece.id, {
        width: newWidth,
        height: newHeight,
        position_x: newX,
        position_y: newY,
      });
    }
  }, [isResizing, resizeStart, zoom, config, isCircle, snapToGrid, onSizeChange, piece.id]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeStart(null);
  }, []);

  // Global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Calculate seat positions - now using absolute offsets from piece center
  const seatPositions = useMemo(() => {
    // Use seat_offsets which now stores absolute positions relative to piece origin
    const offsets = piece.seat_offsets || {};
    const positions = [];
    
    // seats count tells us how many seats to render
    for (let i = 0; i < seats; i++) {
      const offset = offsets[i] || { x: width / 2 - 4, y: -15 }; // Default: center top
      positions.push({
        x: offset.x,
        y: offset.y,
      });
    }
    
    return positions;
  }, [piece.seat_offsets, seats, width]);
  
  // Apply local seat offsets to positions (for live dragging)
  const finalSeatPositions = useMemo(() => {
    return seatPositions.map((pos, idx) => {
      const localOffset = localSeatOffsets[idx];
      // If we have a local offset from dragging, use it
      if (localOffset && (localOffset.x !== undefined || localOffset.y !== undefined)) {
        return {
          x: localOffset.x !== undefined ? localOffset.x : pos.x,
          y: localOffset.y !== undefined ? localOffset.y : pos.y,
          baseX: pos.x,
          baseY: pos.y,
        };
      }
      return {
        x: pos.x,
        y: pos.y,
        baseX: pos.x,
        baseY: pos.y,
      };
    });
  }, [seatPositions, localSeatOffsets]);
  
  // Seat drag handlers - now stores absolute positions
  const handleSeatDragStart = useCallback((e, seatIndex) => {
    if (isGrouped) return; // Don't allow seat dragging when grouped
    e.preventDefault();
    e.stopPropagation();
    
    setIsDraggingSeat(true);
    setDraggedSeatIndex(seatIndex);
    
    // Get current position (either from local offset or from seatPositions)
    const currentPos = finalSeatPositions[seatIndex] || { x: width / 2, y: -15 };
    setSeatDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      startX: currentPos.x,
      startY: currentPos.y,
    });
  }, [isGrouped, finalSeatPositions, width]);
  
  const handleSeatDragMove = useCallback((e) => {
    if (!isDraggingSeat || draggedSeatIndex === null || !seatDragStart) return;
    
    const deltaX = (e.clientX - seatDragStart.mouseX) / zoom;
    const deltaY = (e.clientY - seatDragStart.mouseY) / zoom;
    
    // Store absolute position
    setLocalSeatOffsets(prev => ({
      ...prev,
      [draggedSeatIndex]: {
        x: seatDragStart.startX + deltaX,
        y: seatDragStart.startY + deltaY,
      }
    }));
  }, [isDraggingSeat, draggedSeatIndex, seatDragStart, zoom]);
  
  const handleSeatDragEnd = useCallback(() => {
    if (isDraggingSeat && onSeatOffsetsChange) {
      onSeatOffsetsChange(piece.id, localSeatOffsets);
    }
    setIsDraggingSeat(false);
    setDraggedSeatIndex(null);
    setSeatDragStart(null);
  }, [isDraggingSeat, onSeatOffsetsChange, piece.id, localSeatOffsets]);
  
  // Global mouse listeners for seat dragging
  useEffect(() => {
    if (isDraggingSeat) {
      window.addEventListener('mousemove', handleSeatDragMove);
      window.addEventListener('mouseup', handleSeatDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleSeatDragMove);
        window.removeEventListener('mouseup', handleSeatDragEnd);
      };
    }
  }, [isDraggingSeat, handleSeatDragMove, handleSeatDragEnd]);

  // Render the shape
  const renderShape = () => {
    const strokeColor = piece.stroke_color || (isSelected ? '#3B82F6' : '#1e40af');
    const fillColor = piece.fill_color || '#3b82f6';

    if (config.isArc && config.svgPath) {
      // Arc shape using original SVG path
      return (
        <svg
          width={width * zoom}
          height={height * zoom}
          viewBox={config.viewBox}
          className="absolute inset-0"
          preserveAspectRatio="none"
        >
          <path
            d={config.svgPath}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.5"
          />
        </svg>
      );
    }

    if (isCircle) {
      return (
        <div
          className="w-full h-full border-2 transition-colors duration-150"
          style={{
            borderRadius: '50%',
            borderColor: strokeColor,
            backgroundColor: fillColor,
          }}
        />
      );
    }

    // Rectangle or square
    return (
      <div
        className="w-full h-full border-2 transition-colors duration-150"
        style={{
          borderRadius: '4px',
          borderColor: strokeColor,
          backgroundColor: fillColor,
        }}
      />
    );
  };

  // Calculate display position (including manual drag offset for rotated pieces)
  const displayX = ((piece.position_x || 0) + (isManualDragging ? manualDragOffset.x : 0)) * zoom;
  const displayY = ((piece.position_y || 0) + (isManualDragging ? manualDragOffset.y : 0)) * zoom;

  return (
    <div
      ref={(node) => {
        drag(node);
        pieceRef.current = node;
      }}
      className={`absolute ${isGrouped ? 'cursor-default' : (isRotated ? 'cursor-move' : 'cursor-grab')}`}
      style={{
        left: displayX,
        top: displayY,
        width: width * zoom,
        height: height * zoom,
        transform: piece.rotation ? `rotate(${piece.rotation}deg)` : undefined,
        transformOrigin: 'center center',
        zIndex: isGrouped && isSelected ? 20 : (isSelected ? 10 : (isManualDragging ? 1000 : 1)),
        opacity: isDragging ? 0 : (isManualDragging ? 0.7 : 1),
        transition: isManualDragging ? 'none' : undefined,
        overflow: 'visible', // Allow seats positioned outside bounds to be visible and clickable
      }}
      onClick={(e) => {
        if (isManualDragging) return; // Don't trigger click after drag
        e.stopPropagation();
        if (onClick) onClick(e);
      }}
      onMouseDown={isRotated && !isGrouped ? handleManualDragStart : undefined}
      data-testid={`custom-piece-${piece.id}`}
    >
      {/* Shape */}
      {renderShape()}

      {/* Label */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 5 }}
      >
      </div>

      {/* Seat Indicators with full functionality */}
      {showSeats && finalSeatPositions.map((pos, idx) => {
        // For grouped pieces, check if this seat is occupied by a guest
        const isOccupied = isGrouped && idx < tableGuests.length;
        const guestAtSeat = isOccupied ? tableGuests[idx] : null;
        const isHovered = hoveredSeatIndex === idx;
        const canInteract = isGrouped && !!onSeatClick;
        const canDragSeat = isSelected && !isGrouped && !canInteract;
        const isBeingDragged = draggedSeatIndex === idx;
        
        return (
          <div
            key={idx}
            className="absolute"
            style={{
              left: pos.x * zoom,
              top: pos.y * zoom,
              zIndex: isGrouped ? 100 : (isHovered || isBeingDragged ? 50 : 30), // Higher z-index for grouped seats
            }}
            onMouseEnter={() => setHoveredSeatIndex(idx)}
            onMouseLeave={() => setHoveredSeatIndex(null)}
          >
            {/* Seat dot */}
            <div
              className={`
                rounded-full border transition-all duration-200
                ${isOccupied 
                  ? 'bg-[#D4AF37] border-[#B5952F]' 
                  : 'bg-blue-500/50 border-blue-400'
                }
                ${isSelected ? 'ring-1 ring-white/30' : ''}
                ${canInteract ? 'cursor-pointer hover:scale-125' : ''}
                ${canDragSeat ? 'cursor-grab active:cursor-grabbing hover:scale-125 hover:bg-blue-400' : ''}
                ${canInteract && !isOccupied ? 'hover:bg-[#D4AF37]/50 hover:border-[#D4AF37]' : ''}
                ${canInteract && isOccupied ? 'hover:bg-[#D4AF37] hover:border-[#FFD700]' : ''}
                ${isBeingDragged ? 'scale-150 bg-blue-400 border-blue-300' : ''}
              `}
              style={{
                width: 8 * zoom,
                height: 8 * zoom,
              }}
              onMouseDown={(e) => {
                if (canDragSeat) {
                  handleSeatDragStart(e, idx);
                } else if (canInteract) {
                  e.stopPropagation();
                }
              }}
              onClick={(e) => {
                if (canInteract) {
                  e.stopPropagation();
                  const rect = pieceRef.current?.getBoundingClientRect();
                  onSeatClick({
                    tableId: tableIdForSeats,
                    tableName: tableNameForSeats,
                    seatIndex: idx,
                    posX: rect ? rect.left + pos.x * zoom + 4 * zoom : 0,
                    posY: rect ? rect.top + pos.y * zoom + 8 * zoom : 0,
                    currentGuest: guestAtSeat,
                    isOccupied: isOccupied,
                    pieceId: piece.id,
                  });
                }
              }}
              data-testid={`piece-seat-${idx}${isOccupied ? '-occupied' : '-empty'}`}
            />
            
            {/* Guest name tooltip on hover */}
            {isHovered && guestAtSeat && (
              <div 
                className="absolute z-[100] pointer-events-none whitespace-nowrap"
                style={{
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: 4,
                }}
              >
                <div className="bg-slate-900 border border-slate-700 rounded px-2 py-1 shadow-lg">
                  <p className="text-xs text-white font-medium">
                    {guestAtSeat.full_name}
                  </p>
                </div>
                <div 
                  className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
                  style={{
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '4px solid #1e293b',
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      
      {/* Seat count adjustment buttons (when selected and not grouped) */}
      {isSelected && !isGrouped && onSeatsChange && (
        <div
          className="absolute flex items-center gap-1 bg-slate-800 rounded-full px-1 py-0.5 border border-slate-600 shadow-lg"
          style={{
            bottom: -24 * zoom,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 30,
          }}
        >
          <button
            className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              if (seats > 1) {
                onSeatsChange(piece.id, seats - 1);
              }
            }}
            title="Remove seat"
          >
            -
          </button>
          <span className="text-xs text-white font-medium px-1" style={{ minWidth: 20, textAlign: 'center' }}>
            {seats}
          </span>
          <button
            className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              if (seats < 20) {
                onSeatsChange(piece.id, seats + 1);
              }
            }}
            title="Add seat"
          >
            +
          </button>
        </div>
      )}

      {/* Selection indicator */}
      {isSelected && !isGrouped && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full z-20" />
      )}

      {/* Resize Handles (only when selected and NOT grouped) */}
      {isSelected && !isCircle && !isGrouped && (
        <>
          <ResizeHandle position="nw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="n" onMouseDown={handleResizeStart} />
          <ResizeHandle position="ne" onMouseDown={handleResizeStart} />
          <ResizeHandle position="w" onMouseDown={handleResizeStart} />
          <ResizeHandle position="e" onMouseDown={handleResizeStart} />
          <ResizeHandle position="sw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="s" onMouseDown={handleResizeStart} />
          <ResizeHandle position="se" onMouseDown={handleResizeStart} />
        </>
      )}

      {/* Resize handles for circle (corner only) - NOT when grouped */}
      {isSelected && isCircle && !isGrouped && (
        <>
          <ResizeHandle position="nw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="ne" onMouseDown={handleResizeStart} />
          <ResizeHandle position="sw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="se" onMouseDown={handleResizeStart} />
        </>
      )}

      {/* Rotation Handle (only when selected and NOT grouped) */}
      {isSelected && !isGrouped && (
        <div
          className="absolute flex flex-col items-center"
          style={{
            left: '50%',
            top: -30 * zoom,
            transform: 'translateX(-50%)',
            zIndex: 25,
          }}
        >
          {/* Line connecting to shape */}
          <div 
            className="w-px bg-blue-400"
            style={{ height: 15 * zoom }}
          />
          {/* Rotation handle circle */}
          <div
            className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white cursor-grab active:cursor-grabbing hover:bg-blue-400 transition-colors shadow-md"
            style={{
              marginTop: -2,
            }}
            onMouseDown={handleRotateStart}
            title="Drag to rotate (hold Shift for 15° snap)"
          />
        </div>
      )}

      {/* Floating Action Buttons (only when selected and NOT grouped) */}
      {isSelected && !isGrouped && (
        <>
          {/* Duplicate button - top right */}
          {onDuplicate && (
            <button
              className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg transition-all hover:scale-110 z-30"
              style={{
                top: -10,
                right: -10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(piece);
              }}
              title="Duplicate"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          )}

          {/* Delete button - bottom right */}
          {onDelete && (
            <button
              className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-red-500 hover:bg-red-400 text-white shadow-lg transition-all hover:scale-110 z-30"
              style={{
                bottom: -10,
                right: -10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(piece.id);
              }}
              title="Delete"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}
